/**
 * Spec 200 — live sync hook (Fase 4 #3).
 *
 * Wires the singleton `RelayClient` to the post-pair identity and
 * routes incoming `sync` messages through the same decrypt path the
 * Fase 2 integration test exercises end-to-end:
 *
 *   relay sync (encrypted blob)
 *     → split `ivHex:base64(ct)`        (mobile pre-SEC-21 format)
 *     → AES-CBC-decrypt with teamKey
 *     → JSON.parse → Work
 *     → saveWork (which emits store change)
 *
 * The WorkList + WorkDetail subscribe to the store change emitter,
 * so the user sees the new row land without any manual refresh.
 *
 * This hook is the integration glue — it doesn't own the decrypt
 * primitive (that's `webCrypto.aesCbcDecrypt`) or the storage
 * write (that's `db.saveWork`). Keeping it focused makes the test
 * surface small: mock `relay`, drive `onSync`, assert `saveWork`.
 */
import { useEffect } from 'react';

import { aesCbcEncrypt, bytesToHex, bytesToUtf8, decryptStringAuto, hexToBytes, utf8ToBytes } from '../crypto/webCrypto';
import {
  applyClientDelete,
  applyClientUpsert,
  deleteWork,
  saveWork,
  getWorkById,
} from '../store/db';
import { relay } from '../sync/relay';
import { applyPhotoMessage, isPhotoSyncMsg } from './syncPhotos';
import { applyWorkVoiceNotes } from './syncVoiceNotes';
import { applyRemotePins, getPinnedWorks, subscribeLocalPinChanges } from '../store/pinnedWorks';
import {
  applyRemoteSenderInfo,
  getSenderInfo as getLocalSenderInfo,
} from '../store/senderInfo';
import type { WebIdentity } from '../store/keys';
import type { Work } from '../types';

/**
 * Decrypt + apply a single sync blob. Exported for tests; in the hook
 * it's called from the relay handler.
 *
 * Wire shape (matches mobile/src/sync/syncWorks.ts byte-for-byte):
 *   AES-CBC-256(teamKey, iv, JSON.stringify({
 *     type: 'upsert' | 'delete',
 *     work?: Work,
 *     workId?: string,
 *     timestamp: number,
 *   }))
 *   wrapped as `ivHex:base64(ct)`.
 *
 * Last-write-wins: when the upsert arrives we compare `serverTs`
 * (preferred — relay's clock, immune to client skew) against the
 * locally-stored work's same fields. The remote wins only when its
 * timestamp is strictly greater. This mirrors mobile's
 * `processSync` semantics exactly so a mobile↔web edit race
 * resolves the same way regardless of which side it lands on.
 */
export async function applySyncBlob(
  blob: string,
  teamKeyHex: string,
  serverTs?: number,
  // Optional photo-fetch credentials. When omitted (e.g. the
  // unit-test path that exercises only the work pipeline) photo
  // messages are ignored, preserving backwards compatibility with
  // the original signature.
  companyToken?: string,
  // Optional self technicianId — used to filter `pins_sync` messages
  // so each device only applies pin updates that belong to its own
  // user. When omitted, `pins_sync` is ignored (test paths that
  // don't drive the pin pipeline keep working unchanged).
  selfTechnicianId?: string,
): Promise<void> {
  // SEC-21 Phase A — `decryptStringAuto` detects the wire format
  // (`g1:` prefix → GCM, otherwise legacy CBC) so this hook keeps
  // reading what older mobile peers write while it can also accept
  // GCM blobs the day Phase B flips on the writers.
  if (!blob.includes(':')) {
    // Not a wire shape we recognise — drop silently.
    return;
  }
  const teamKey = hexToBytes(teamKeyHex);

  let plaintextBytes: Uint8Array;
  try {
    plaintextBytes = await decryptStringAuto(teamKey, blob);
  } catch {
    return;
  }
  const json = bytesToUtf8(plaintextBytes);

  const parsed = JSON.parse(json);

  // pins_sync — per-user pin list broadcast. Each device of the same
  // logged-in user mirrors the set; devices belonging to other users
  // (other technicianIds within the team) ignore the message. LWW by
  // relay delivery order is enough — the relay timestamps every blob.
  if (
    parsed &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed) &&
    (parsed as { type?: unknown }).type === 'pins_sync' &&
    Array.isArray((parsed as { pinnedIds?: unknown }).pinnedIds) &&
    typeof (parsed as { userId?: unknown }).userId === 'string'
  ) {
    const msg = parsed as { type: 'pins_sync'; userId: string; pinnedIds: string[] };
    if (selfTechnicianId && msg.userId === selfTechnicianId) {
      applyRemotePins(msg.pinnedIds);
    }
    return;
  }

  // sync_full from mobile (sync_request response): a raw Work[] array.
  // Mobile sends `JSON.stringify(works)` after `respondSync(requesterId, ...)`.
  // We persist each one with the same staleness check as live `upsert`.
  if (Array.isArray(parsed)) {
    for (const incoming of parsed as Work[]) {
      if (!incoming || !incoming.id) continue;
      const remoteTs = serverTs ?? incoming.updatedAt;
      const local = await getWorkById(incoming.id);
      if (local) {
        const localTs = local.serverTs ?? local.updatedAt;
        if (remoteTs <= localTs) continue;
      }
      if (serverTs !== undefined) incoming.serverTs = serverTs;
      await saveWork(incoming);
      if (companyToken && incoming.voiceNotes && incoming.voiceNotes.length > 0) {
        void applyWorkVoiceNotes(incoming, teamKeyHex, companyToken);
      }
    }
    return;
  }

  const message = parsed as {
    type: string;
    work?: Work;
    workId?: string;
    timestamp?: number;
  };

  if (message.type === 'upsert' && message.work) {
    const incoming = message.work;
    // Mobile parity: prefer relay's serverTs, fall back to
    // payload.updatedAt. Compare against the local row to skip
    // stale upserts.
    const remoteTs = serverTs ?? incoming.updatedAt;
    const local = await getWorkById(incoming.id);
    if (local) {
      const localTs = local.serverTs ?? local.updatedAt;
      if (remoteTs <= localTs) return; // skip
    }
    if (serverTs !== undefined) incoming.serverTs = serverTs;
    await saveWork(incoming);
    // SYNC-W1 voice notes — voice-note metadata is embedded in the
    // Work blob; persist to the dedicated store and lazily fetch
    // each audio binary. Fire-and-forget; the per-note fetches run
    // in the background and emit store changes when they patch.
    if (companyToken && incoming.voiceNotes && incoming.voiceNotes.length > 0) {
      void applyWorkVoiceNotes(incoming, teamKeyHex, companyToken);
    }
    return;
  }

  if (message.type === 'delete' && message.workId) {
    await deleteWork(message.workId);
    return;
  }

  // Branding ("Identidad de empresa") sync — LWW by `updatedAt`.
  // Mirrors mobile's `processSenderInfoSync` semantics: a strictly
  // newer remote copy overwrites the local row; equal/older payloads
  // are ignored (avoids re-applying our own echoed broadcast).
  if (message.type === 'branding_sync') {
    const m = parsed as {
      type: 'branding_sync';
      companyName?: unknown;
      taxId?: unknown;
      phone?: unknown;
      email?: unknown;
      web?: unknown;
      logoUri?: unknown;
      updatedAt?: unknown;
    };
    if (typeof m.companyName !== 'string' || typeof m.updatedAt !== 'number') return;
    const local = await getLocalSenderInfo();
    const localTs = local?.updatedAt ?? 0;
    if (m.updatedAt <= localTs) return;
    await applyRemoteSenderInfo({
      companyName: m.companyName,
      taxId: typeof m.taxId === 'string' ? m.taxId : undefined,
      phone: typeof m.phone === 'string' ? m.phone : undefined,
      email: typeof m.email === 'string' ? m.email : undefined,
      web: typeof m.web === 'string' ? m.web : undefined,
      logoUri: typeof m.logoUri === 'string' ? m.logoUri : undefined,
      updatedAt: m.updatedAt,
    });
    return;
  }

  // Address-book sync (mobile/src/sync/syncClients.ts wire shape).
  // Live broadcasts only — the relay's `sync` channel is shared with
  // works, and the dispatcher branches on `type`. Last-write-wins is
  // enforced inside `applyClientUpsert` (compares `updatedAt`).
  if (message.type === 'client_upsert') {
    const c = (message as { client?: unknown }).client;
    if (c && typeof c === 'object' && typeof (c as { id?: unknown }).id === 'string') {
      await applyClientUpsert(c as import('../types').Client);
    }
    return;
  }
  if (message.type === 'client_delete') {
    const id = (message as { clientId?: unknown }).clientId;
    if (typeof id === 'string') {
      await applyClientDelete(id);
    }
    return;
  }

  // SYNC-W1 — photo sync. Lazily fire the fetch + decrypt + savePhoto
  // pipeline; only the hook (which has identity) supplies a token.
  if (isPhotoSyncMsg(message) && companyToken) {
    await applyPhotoMessage(message, teamKeyHex, companyToken);
    return;
  }
  // Unknown / malformed type — drop silently.
}

/**
 * Connect the relay singleton with the paired identity and apply
 * incoming sync messages. Disconnects on unmount.
 */
export function useLiveSync(identity: WebIdentity | null): void {
  useEffect(() => {
    if (!identity) return;
    // The relay maps `workerId` 1:1 to a websocket: a second
    // identify with the same id evicts the first. If the web used
    // the admin's `technicianId` directly, mobile and web would
    // ping-pong each other off the live-sync channel forever.
    // We compose a distinct worker id from the technicianId + a
    // prefix of the per-browser terminalId so the relay treats the
    // pairing as two co-existing devices of the same admin. The
    // company token + role propagate from the paired identity, so
    // the relay still authorises broadcasts the same way.
    const webWorkerId = `${identity.technicianId}-web-${identity.terminalId.slice(0, 8)}`;
    relay.connect({
      companyId: identity.companyId,
      workerId: webWorkerId,
      name: `${identity.name} (web)`,
      isAdmin: identity.isAdmin,
      role: identity.role,
      companyToken: identity.companyToken,
    });
    const handleBlob = async (encryptedData: string, serverTs?: number) => {
      try {
        await applySyncBlob(
          encryptedData,
          identity.teamKey,
          serverTs,
          identity.companyToken,
          identity.technicianId,
        );
      } catch {
        // A single corrupt blob shouldn't tear the WS down. The
        // relay will keep delivering the rest; we just log nothing
        // (production error reporting can hook in here later).
      }
    };
    const offSync = relay.onSync(handleBlob);
    // sync_full = response to our sync_request (full history dump from
    // mobile). Same blob format as live `sync` broadcasts, so route
    // both through the same applySyncBlob pipeline.
    const offSyncFull = relay.onSyncFull((encryptedData) => handleBlob(encryptedData));

    // Outgoing pins_sync — debounced 500ms after the last local
    // toggle/unpin so a flurry of clicks coalesces into a single
    // broadcast. Subscribe to LOCAL changes only; remote-applied pin
    // updates fire `_uiListeners` but skip the local-change pool, so
    // they cannot bounce back as fresh broadcasts (avoids infinite
    // ping-pong between two devices of the same user).
    let pinDebounce: ReturnType<typeof setTimeout> | null = null;
    const offLocalPins = subscribeLocalPinChanges(() => {
      if (pinDebounce) clearTimeout(pinDebounce);
      pinDebounce = setTimeout(() => {
        pinDebounce = null;
        const pinnedIds = [...getPinnedWorks()];
        const payload = JSON.stringify({
          type: 'pins_sync',
          userId: identity.technicianId,
          pinnedIds,
        });
        // Match the web's outgoing wire format used by
        // `sync/syncWork.ts`: AES-CBC-256 over JSON, framed as
        // `ivHex:base64(ct)` (no `g1:` prefix). Mobile's
        // `decryptStringAuto` accepts both this and the GCM frame,
        // so peers running either codepath can read it.
        (async () => {
          try {
            const teamKey = hexToBytes(identity.teamKey);
            const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
            const ct = await aesCbcEncrypt(teamKey, utf8ToBytes(payload), iv);
            let ctBin = '';
            for (let i = 0; i < ct.length; i++) ctBin += String.fromCharCode(ct[i]);
            const blob = `${bytesToHex(iv)}:${btoa(ctBin)}`;
            relay.send(blob);
          } catch {
            // A failed encrypt/send should not crash the app — the
            // pin is already persisted locally; the next local
            // change re-arms the debounce + retries.
          }
        })();
      }, 500);
    });

    return () => {
      offSync();
      offSyncFull();
      offLocalPins();
      if (pinDebounce) {
        clearTimeout(pinDebounce);
        pinDebounce = null;
      }
      relay.disconnect();
    };
  }, [identity]);
}
